import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { Header } from './Header/Header';
import {
    AnalyticsTargetLayerProvider,
    mqDown,
    mqUp,
    useAnalyticsClient,
    useAnalyticsPingEvent,
} from 'rwl-ui';
import { MapView } from './Map/MapView';
import { FeaturedContent } from '../Layout/FeaturedContent';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    ContentDetailsProvider,
    useBreakpoint,
    useContainerWidth,
} from '../providers';
import { useJob } from '../fetchers';
import { useParentPageIntersection } from '../hooks/intersectionObservers/useParentPageIntersection';
import { WidgetContainerIntersectionProvider } from '../hooks/intersectionObservers/useWidgetContainerIntersection';
import { Title } from './Title';
import { useAnalyticsAppLayer } from '../hooks/useAnalyticsProviders';
import {
    ScrollProvider,
    useExternalZipFormListener,
    useGlobal,
    usePostCode,
} from '../hooks';
import { TargetLayer } from 'rwl-shared/dist/models/analytics';
import DirectGate from '../components/ZipCodeGate/DirectGate';

const FIRST_ENGAGEMENT_TIMEOUT = 3000;

type TimeoutType = ReturnType<typeof setTimeout>;

const ScreenContainer = styled.div<{
    isSmall: boolean;
    isBumpOutEnabled?: boolean;
}>`
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    ${({ isSmall, isBumpOutEnabled }) =>
        isSmall
            ? `
                overflow: unset;
                position: relative;
            `
            : isBumpOutEnabled
            ? `
                inset: 0;
            `
            : `
                inset: 0;
                overflow: hidden;
            `}
    container-type: inline-size;
    container-name: container;
`;

const WidgetContent = styled.div<{
    isSmall: boolean;
    isBumpOutEnabled?: boolean;
}>`
    ${({ isSmall, isBumpOutEnabled }) =>
        isSmall
            ? `
                overflow: unset;
            `
            : isBumpOutEnabled
            ? `
                inset: 0;
                overflow: hidden;
                flex: 1;
            `
            : `
                inset: 0;
                overflow: hidden;
                border: 1px solid var(--rwl-background-level2);
                border-radius: var(--rwl-radius-sm);
                flex: 1;
            `}
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    position: relative;
    background: white;
    ${({ isBumpOutEnabled }) =>
        !isBumpOutEnabled && `border-radius: var(--rwl-radius-sm);`}
`;

const GLOBAL_CSS = css`
    :root {
        --rwl-sidebar-width: 240px;
        --rwl-header-height: 70px;
        --rwl-xs-header-height: 155px;
        --rwl-minimum-screen-size: 320px;
        --rwl-media-modal-zIndex: 30;
        box-sizing: border-box;
        --rwl-border-outline: 1px solid #e4e4e7;
    }
    .rwl-card-title {
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 157%;
        margin: 0;
        margin-bottom: var(--rwl-spacing-xs);
    }

    .rwl-body-2 {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 143%;
    }

    *,
    *:before,
    *:after {
        box-sizing: inherit;
    }

    *::-webkit-scrollbar {
        width: 12px; /* width of the entire scrollbar */
    }

    *::-webkit-scrollbar-track {
        background-color: transparent; /* color of the tracking area */
    }

    *::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2); /* color of the scroll thumb */
        border-radius: 6px; /* roundness of the scroll thumb */
        width: 8px;
    }

    body {
        position: relative;
        margin: 0;
        -webkit-overflow-scrolling: auto;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;

        width: 100%;
        min-width: var(--rwl-minimum-screen-size);
        height: 100%; /* ios Safari measures 100vh to entire screen even if safari nav takes up some */
    }
`;

const MainContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    ${mqUp('sm')} {
        overflow: hidden;
    }
    position: relative;
    ${mqDown('sm')} {
        & > :last-child {
            display: none;
        }
    }
    margin-left: var(--rwl-spacing-md);
`;

const MapSection = styled.div`
    flex: 1;
    width: 100%;
    border-left: 1px solid var(--rwl-background-level2);
`;

const PING_INTERVAL = 10_000;

// TODO: All the ping logic could probably be lifted into it's own custom hook, but I've run out of time.  C'est la vie

export const Layout = (): JSX.Element => {
    const { bumpOutTestEnabled, bumpOutTest } = useGlobal();
    const requiresPostCode = bumpOutTest?.requiresPostCode || false;
    const tabBackgroundColor = bumpOutTest?.tabBackgroundColor;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIsInView = useRef(false); // Using a ref not state so we don't re-render and so we can access the value imperatively
    const [showGate, setShowGate] = useState(requiresPostCode);
    const firstViewTimer = useRef<TimeoutType | null | undefined>(undefined); // Undefined = not triggered, null = completed, TimeoutType = in progress
    const { ref: widthRef } = useContainerWidth();
    const postCode = usePostCode();
    const { isBelowBreakpoint } = useBreakpoint();
    const { sendAllEventsNow } = useAnalyticsClient();
    const sendPingEvent = useAnalyticsPingEvent();
    const { targetRef } = useParentPageIntersection(
        () => {
            setIsInView(true);
        },
        () => {
            setIsInView(false);
        },
        { silenceMissingProviderError: true, threshold: 0.5 }
    );
    const { job } = useJob();
    const layer = useAnalyticsAppLayer();

    useExternalZipFormListener(Boolean(bumpOutTestEnabled));

    const setIsInView = (isInView: boolean) => {
        widgetIsInView.current = isInView;
        // The first time the widget stays in view for 3+ seconds send a special ping event
        if (isInView && firstViewTimer.current === null) {
            // If the timer has previously completed, don't do anything
            return;
        } else if (isInView && firstViewTimer.current === undefined) {
            // Start the timer
            firstViewTimer.current = setTimeout(() => {
                // Send the event and mark the timer as completed
                sendPingEvent({
                    widgetIsInView: isInView,
                    userHasViewed: true,
                });
                sendAllEventsNow();
                firstViewTimer.current = null;
            }, FIRST_ENGAGEMENT_TIMEOUT);
        } else if (!isInView && firstViewTimer.current) {
            // If the timer is in progress and the widget goes out of view, cancel the timer
            clearTimeout(firstViewTimer.current);
            firstViewTimer.current = undefined;
        }
    };

    // Cleanup the timer when the component is unmounted
    useEffect(() => {
        return () => {
            if (firstViewTimer.current) {
                clearTimeout(firstViewTimer.current);
            }
        };
    }, []);

    const setContainerRef = (node: HTMLDivElement | null) => {
        containerRef.current = node;
        widthRef(node);
        targetRef(node);
    };

    // Send a ping event every 10 seconds once the widget is mounted
    useEffect(() => {
        const interval = setInterval(() => {
            sendPingEvent({ widgetIsInView: widgetIsInView.current });
        }, PING_INTERVAL);

        return () => clearInterval(interval);
    }, [sendPingEvent]);

    const targetLayerId = useMemo(() => {
        return layer === TargetLayer.SERVICE_AREA ? postCode : job?.id;
    }, [layer, postCode, job]);

    return (
        <ScreenContainer
            ref={setContainerRef}
            isSmall={isBelowBreakpoint('sm')}
            isBumpOutEnabled={bumpOutTestEnabled}
        >
            {/* ZIP code gate displayed when requiresPostCode is true */}
            {showGate && (
                <AnalyticsTargetLayerProvider
                    targetLayer={TargetLayer.ZIPCODE_GATE}
                >
                    <DirectGate
                        onClose={() => setShowGate(false)}
                        tabBackgroundColor={tabBackgroundColor}
                    />
                </AnalyticsTargetLayerProvider>
            )}
            <WidgetContainerIntersectionProvider
                value={{ rootEl: containerRef.current }}
            >
                <AnalyticsTargetLayerProvider
                    targetLayer={layer}
                    targetLayerId={targetLayerId}
                >
                    <Title />
                    <WidgetContent
                        id="widget-content"
                        isSmall={isBelowBreakpoint('sm')}
                        isBumpOutEnabled={bumpOutTestEnabled}
                    >
                        <Global styles={GLOBAL_CSS} />
                        <Header />
                        <ContentDetailsProvider>
                            <MainContent>
                                <ScrollProvider>
                                    <FeaturedContent />
                                </ScrollProvider>
                                <MapSection>
                                    <MapView />
                                </MapSection>
                            </MainContent>
                        </ContentDetailsProvider>
                    </WidgetContent>
                </AnalyticsTargetLayerProvider>
            </WidgetContainerIntersectionProvider>
        </ScreenContainer>
    );
};
